import { useQueries, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { broadcastV2ListQueryOptions } from "@/api/broadcastV2Audiences";
import { byocEventsQueryOptions } from "@/api/connectors/byocEvents";
import {
  dragonTilesByTypeQueryOptions,
  dragonTilesQueryOptions,
} from "@/api/dragon-tiles";
import { formatByocEventName } from "@/features/Connectors/byocEventName";
import {
  isByocDragonTile,
  resolveDragonTileDisplayName,
  useSiteName,
} from "@/features/Connectors/byocTileDisplayName";
import { TilesCountHexagon } from "@/features/Connectors/TilesCountHexagon";
import { isComingSoonForViewer } from "@/features/DragonTiles/comingSoon";
import { HoneycombGrid } from "@/features/DragonTiles/components/HoneycombGrid/HoneycombGrid";
import { ParentTile } from "@/features/DragonTiles/components/ParentTile/ParentTile";
import { isBroadcastDragonTile } from "@/features/DragonTiles/status/rollup";
import {
  hasElevatedAccessValue,
  usePrivilegedModeStore,
  useHasElevatedAccess,
} from "@/utils/privilegedMode";

// Broadcast statuses that mean a send is in flight; the Broadcast tile renders
// as "Running" when any broadcast matches one of these.
const BROADCAST_RUNNING_STATUSES = [
  "BROADCAST_STATUS_ACTIVE",
  "BROADCAST_STATUS_IN_PROGRESS",
] as const;

export const Route = createFileRoute("/_auth/dragon-tiles/$siteId/")({
  component: DragonTilesGrid,
  loader: async ({ params, context }) => {
    const privilegedModeDisabled = usePrivilegedModeStore.getState().disabled;
    const hasElevatedAccess = hasElevatedAccessValue(
      context.auth?.user?.user?.role,
      privilegedModeDisabled,
    );
    if (!hasElevatedAccess) return;

    await Promise.allSettled([
      context.queryClient.ensureQueryData(
        dragonTilesByTypeQueryOptions(params.siteId, "byoc"),
      ),
      context.queryClient.ensureQueryData(
        byocEventsQueryOptions(params.siteId),
      ),
    ]);
  },
});

export function DragonTilesGrid() {
  const { siteId } = Route.useParams();
  const hasElevatedAccess = useHasElevatedAccess();
  const byocTileName = useSiteName(siteId);
  const byocEnabled = hasElevatedAccess;

  const { data: dragonTiles } = useSuspenseQuery({
    ...dragonTilesQueryOptions(siteId),
  });
  const { data: byocTiles = [] } = useQuery({
    ...dragonTilesByTypeQueryOptions(siteId, "byoc"),
    enabled: byocEnabled,
  });
  const { data: byocEvents } = useQuery({
    ...byocEventsQueryOptions(siteId),
    enabled: byocEnabled,
  });
  const enabledByocEvents = (byocEvents ?? [])
    .filter((event) => event.enabled)
    .map(({ id, name, enabled }) => ({
      id,
      name: formatByocEventName(name),
      enabled,
    }));
  const byocEventToggles = byocEvents?.map(({ id, name, enabled }) => ({
    id,
    name: formatByocEventName(name),
    enabled,
  }));
  const runningBroadcastQueries = useQueries({
    queries: BROADCAST_RUNNING_STATUSES.map((status) => ({
      ...broadcastV2ListQueryOptions(siteId, { status, page: 1, limit: 1 }),
    })),
  });

  const isBroadcastRunning = runningBroadcastQueries.some(
    (query) => (query.data?.broadcasts?.length ?? 0) > 0,
  );

  // Reserve the last visible slot for BYOC.
  const dragonTilesWithoutSpecialCases = dragonTiles.filter(
    (tile) => !isByocDragonTile(tile),
  );

  const dragonTilesWithBroadcastState = dragonTilesWithoutSpecialCases.map(
    (tile) => {
      if (isBroadcastRunning && isBroadcastDragonTile(tile)) {
        return {
          ...tile,
          is_active: true,
        };
      }

      return tile;
    },
  );

  // Tiles that are not ready yet stay visible but inert for non-elevated users.
  const dragonTilesWithComingSoonState = dragonTilesWithBroadcastState.map(
    (tile) =>
      isComingSoonForViewer(tile.slug, hasElevatedAccess)
        ? { ...tile, is_clickable: false, is_activatable: false }
        : tile,
  );

  const byocTileFromApi = byocEnabled ? byocTiles[0] : undefined;
  const reservedSlotTileForDisplay = byocTileFromApi
    ? {
        ...byocTileFromApi,
        name: resolveDragonTileDisplayName(byocTileFromApi, byocTileName),
        is_clickable: true,
      }
    : dragonTilesWithoutSpecialCases[0]
      ? {
          ...dragonTilesWithoutSpecialCases[0],
          id: "byoc",
          name: byocTileName,
          slug: "byoc",
          category: "byoc",
          order: 19,
          is_clickable: false,
          config: {},
          inner_tiles: [],
          needs_attention: [],
          is_active: false,
        }
      : null;

  const dragonTilesForDisplay = reservedSlotTileForDisplay
    ? [
        ...dragonTilesWithComingSoonState.slice(0, 19),
        reservedSlotTileForDisplay,
      ]
    : dragonTilesWithComingSoonState.slice(0, 20);

  return (
    <HoneycombGrid
      tiles={dragonTilesForDisplay}
      renderTile={(tile) => (
        <ParentTile
          tile={tile}
          siteId={siteId}
          isComingSoon={isComingSoonForViewer(tile.slug, hasElevatedAccess)}
          framesLoaded
          events={isByocDragonTile(tile) ? byocEventToggles : undefined}
          // The BYOC tile swaps its default star glyph for the active-event
          // counter, but only once at least one event is enabled.
          topAdornment={
            isByocDragonTile(tile) && enabledByocEvents.length > 0 ? (
              <TilesCountHexagon
                count={enabledByocEvents.length}
                events={enabledByocEvents}
              />
            ) : undefined
          }
        />
      )}
    />
  );
}
